import { memo, useCallback } from 'react';
import Leaf from '@root/lib/components/Icons/Leaf';
import './Filters.scss';

/**
 * @typedef {Object} ActiveFilter 
 * @property {string} category
 * @property {string} value
 * 
 * @param {{
 *   open: boolean;
 *   activeFilters: Array<ActiveFilter>;
 *   filters: Array<{
 *     category: string;
 *     values: string[];
 *   }>;
 *   onChange: (activeFilters: Array<ActiveFilter>) => void;
 * }} props 
 */
const Filters = ({
  activeFilters,
  filters,
  onChange,
}) => {

  /**
   * Adds/removes an active filter depending on the flag and passes the new array to
   * onChange
   * 
   * @type {(
   *   flag: boolean,
   *   category: string,
   *   value: string
   * ) => void}
   */
  const setFilter = useCallback((flag, category, value) => {
    const newActiveFilters = flag
      ? [...activeFilters, {category, value}]
      : activeFilters.filter(af => `${af.category}.${af.value}` !== `${category}.${value}`);
    onChange(newActiveFilters);
  }, [onChange, activeFilters]);
  
  /**
   * Checks if a filter is active
   * 
   * @type {(category: string, value: string) => boolean}
   */
  const isActive = useCallback((category, value) => {
    return !!activeFilters.find(af => af.category === category && af.value === value);
  }, [activeFilters]);

  return (
    <div className="MealSelectionFilters">
      {filters.map(({ category, values }) =>
        <section className="MealSelectionFilters__Category" key={category}>
          <h4 className="MealSelectionFilters__CategoryTitle">{category}</h4>
          {category.toLowerCase() === 'allergen' && (
            <div className="MealSelectionFilters__CategoryMessage">
              <Leaf />
              <p>Every Metabolic meal is free of gluten, wheat, soy, refined sugars, artificial colors and artificial flavors.</p>
            </div>
          )}
          <ul className="MealSelectionFilters__FilterValues">
            {values.map(value =>
              <li className="MealSelectionFilters__FilterValue" key={value}>
                <input
                  id={`${category}-${value}`}
                  className="MealSelectionFilters__FilterInput"
                  type="checkbox"
                  checked={isActive(category, value)}
                  onChange={(e) => setFilter(e.target.checked, category, value)}
                />
                <label className="MealSelectionFilters__FilterLabel" htmlFor={`${category}-${value}`}>{value}</label>
              </li>
            )}
          </ul>
        </section>
      )}
    </div>
  )
}

export default memo(Filters);